import { LOCALES, type Locale } from '../i18n'
import type { Selection } from '../types'

export type SemanticRouteKind =
  | 'index'
  | 'municipality_profile'
  | 'municipality_indicator'
  | 'trust'
  | 'topics'
  | 'geography'
  | 'rankings'
  | 'stories'
  | 'data'
  | 'collections'
  | 'reuse'
  | 'indicator'

const SEMANTIC_ROUTE_KINDS: readonly SemanticRouteKind[] = [
  'index',
  'municipality_profile',
  'municipality_indicator',
  'trust',
  'topics',
  'geography',
  'rankings',
  'stories',
  'data',
  'collections',
  'reuse',
  'indicator',
]

export interface PublicRouteState {
  schemaVersion: '1.0.0'
  pageType: 'semantic' | 'atlas' | 'embed'
  semanticKind?: SemanticRouteKind
  locale: Locale
  selectedCode?: string
  canonicalPath: string
  alternatePaths: Record<Locale, string>
  selection: Partial<Selection>
  document: {
    title: string
    description: string
  }
}

export const NEUTRAL_ATLAS_PATHS: Record<Locale, string> = {
  en: '/en/map/',
  fr: '/fr/carte/',
  nl: '/nl/kaart/',
  de: '/de/karte/',
}

export const EMBED_ATLAS_PATHS: Record<Locale, string> = {
  en: '/en/embed/',
  fr: '/fr/integrer/',
  nl: '/nl/insluiten/',
  de: '/de/einbetten/',
}

const ATLAS_DOCUMENTS: Record<Locale, { title: string; description: string }> = {
  en: {
    title: 'Interactive atlas of Belgian statistics | Belgium in Maps',
    description: 'Explore official Belgian statistics on an interactive, source-backed map.',
  },
  fr: {
    title: 'Atlas interactif des statistiques belges | Belgium in Maps',
    description: 'Explorez les statistiques officielles belges sur une carte interactive et documentée.',
  },
  nl: {
    title: 'Interactieve atlas van Belgische statistieken | Belgium in Maps',
    description: 'Verken officiële Belgische statistieken op een interactieve kaart met duidelijke bronnen.',
  },
  de: {
    title: 'Interaktiver Atlas belgischer Statistiken | Belgium in Maps',
    description: 'Entdecken Sie offizielle belgische Statistiken auf einer interaktiven, belegten Karte.',
  },
}

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && LOCALES.includes(value as Locale)
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function isApplicationPath(value: unknown): value is string {
  return typeof value === 'string'
    && /^\/(?!\/)(?:[a-z0-9-]+\/)+$/i.test(value)
}

/**
 * Reads build-time route state from a non-executable JSON contract. Content
 * pages use it to reject mismatched legacy atlas queries; neutral atlas pages
 * use it to choose locale and initial state without executing inline code.
 */
export function embeddedPublicRoute(documentRef: Document = document): unknown {
  const element = documentRef.getElementById('belgium-maps-route')
  if (!(element instanceof HTMLScriptElement) || element.type !== 'application/json') return null
  try {
    return JSON.parse(element.textContent || 'null')
  } catch {
    return null
  }
}

export function readPublicRoute(value: unknown = embeddedPublicRoute()): PublicRouteState | null {
  if (
    !isPlainRecord(value)
    || value.schemaVersion !== '1.0.0'
    || !['semantic', 'atlas', 'embed'].includes(String(value.pageType))
    || !isLocale(value.locale)
  ) return null
  if (
    value.semanticKind !== undefined
    && !SEMANTIC_ROUTE_KINDS.includes(value.semanticKind as SemanticRouteKind)
  ) return null
  if (
    (value.selectedCode !== undefined && (
      typeof value.selectedCode !== 'string' || !/^\d{5}$/.test(value.selectedCode)
    ))
    || !isApplicationPath(value.canonicalPath)
    || !isPlainRecord(value.alternatePaths)
    || !isPlainRecord(value.selection)
    || !isPlainRecord(value.document)
    || typeof value.document.title !== 'string'
    || !value.document.title.trim()
    || typeof value.document.description !== 'string'
    || !value.document.description.trim()
  ) return null
  const rawAlternatePaths = value.alternatePaths as Record<string, unknown>
  const alternatePaths = Object.fromEntries(LOCALES.map((locale) => [locale, rawAlternatePaths[locale]]))
  if (LOCALES.some((locale) => (
    !isApplicationPath(alternatePaths[locale])
    || !alternatePaths[locale].startsWith(`/${locale}/`)
  ))) return null
  if (value.canonicalPath !== alternatePaths[value.locale]) return null
  if (value.pageType === 'atlas' && LOCALES.some((locale) => alternatePaths[locale] !== NEUTRAL_ATLAS_PATHS[locale])) {
    return null
  }
  if (value.pageType === 'embed' && LOCALES.some((locale) => alternatePaths[locale] !== EMBED_ATLAS_PATHS[locale])) {
    return null
  }
  if (['atlas', 'embed'].includes(String(value.pageType)) && value.semanticKind !== undefined) return null
  if (
    (['municipality_profile', 'municipality_indicator'].includes(String(value.semanticKind)) && value.selectedCode === undefined)
    || (value.semanticKind !== undefined
      && !['municipality_profile', 'municipality_indicator'].includes(String(value.semanticKind))
      && value.selectedCode !== undefined)
    || (value.semanticKind === 'index' && value.canonicalPath !== `/${value.locale}/`)
  ) return null
  if (value.selectedCode !== undefined && (
    value.pageType !== 'semantic'
    || value.selection.level !== 'municipality'
    || value.canonicalPath === `/${value.locale}/`
  )) return null
  return value as unknown as PublicRouteState
}

/** Query-string state wins over path state, but undefined query keys never
 * erase the meaningful state embedded in a crawlable route. */
export function mergePublicRouteSelection(
  route: PublicRouteState | null,
  querySelection: Partial<Selection>,
): Partial<Selection> {
  const definedQuery = Object.fromEntries(
    Object.entries(querySelection).filter(([, value]) => value !== undefined),
  ) as Partial<Selection>
  return {
    ...(route?.selection ?? {}),
    ...definedQuery,
    filters: definedQuery.filters ?? route?.selection.filters,
  }
}

export function localeAlternatePath(route: PublicRouteState | null, locale: Locale): string | null {
  return route?.alternatePaths[locale] ?? null
}

export function neutralAtlasPath(locale: Locale): string {
  return NEUTRAL_ATLAS_PATHS[locale]
}

export function activeAtlasPath(route: PublicRouteState | null, locale: Locale): string {
  return route?.pageType === 'embed' ? EMBED_ATLAS_PATHS[locale] : neutralAtlasPath(locale)
}

export function neutralAtlasUrl(
  locale: Locale,
  search: string,
  hash = '',
): string {
  return `${neutralAtlasPath(locale)}${search}${hash}`
}

export function writeNeutralAtlasHistory(
  windowRef: Pick<Window, 'history' | 'location'>,
  mode: 'push' | 'replace',
  locale: Locale,
  search: string,
): string {
  const url = neutralAtlasUrl(locale, search, windowRef.location.hash)
  if (mode === 'push') windowRef.history.pushState({}, '', url)
  else windowRef.history.replaceState({}, '', url)
  return url
}

export function writeActiveAtlasHistory(
  windowRef: Pick<Window, 'history' | 'location'>,
  mode: 'push' | 'replace',
  locale: Locale,
  search: string,
  route: PublicRouteState | null,
): string {
  const url = `${activeAtlasPath(route, locale)}${search}${windowRef.location.hash}`
  if (mode === 'push') windowRef.history.pushState({}, '', url)
  else windowRef.history.replaceState({}, '', url)
  return url
}

const ATLAS_STATE_KEYS = new Set([
  'metric', 'level', 'measure', 'period', 'variant', 'palette', 'colors', 'classes', 'area', 'lang',
  'scope', 'encoding', 'borders', 'view',
])

/** A direct request for a semantic article stays an article only when every
 * atlas-state key in its query agrees with the page contract. Old or copied
 * arbitrary atlas URLs on semantic paths are moved to the neutral atlas. */
export function contentPageAtlasRedirect(
  route: PublicRouteState | null,
  location: Pick<Location, 'search' | 'hash'>,
): string | null {
  if (!route || route.pageType !== 'semantic') return null
  const params = new URLSearchParams(location.search)
  const hasAtlasState = [...params.keys()].some((key) => ATLAS_STATE_KEYS.has(key) || key.startsWith('f.'))
  if (!hasAtlasState) return null

  const expected = {
    metric: route.selection.metricId,
    level: route.selection.level,
    measure: route.selection.measureId,
    period: route.selection.period,
    variant: route.selection.variant ?? 'default',
    area: route.selectedCode,
    lang: route.locale,
  }
  const agrees = [...params.entries()].every(([key, value]) => {
    if (key.startsWith('f.') || ['palette', 'colors', 'classes', 'scope', 'encoding', 'borders', 'view'].includes(key)) return false
    if (!ATLAS_STATE_KEYS.has(key)) return true
    return expected[key as keyof typeof expected] === value
  })
  if (agrees) return null
  const requestedLocale = params.get('lang')
  const locale = isLocale(requestedLocale) ? requestedLocale : route.locale
  return neutralAtlasUrl(locale, location.search, location.hash)
}

function ensureMeta(documentRef: Document, selector: string, attributes: Record<string, string>): HTMLElement {
  let element = documentRef.head.querySelector<HTMLElement>(selector)
  if (!element) {
    element = documentRef.createElement(selector.startsWith('link') ? 'link' : 'meta')
    documentRef.head.append(element)
  }
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value)
  return element
}

/** Keep a history-routed atlas document explicitly non-indexable and prevent
 * stale municipality canonical/social metadata after a state transition. */
export function applyNeutralAtlasMetadata(
  documentRef: Document,
  locale: Locale,
  canonicalOrigin: string,
): void {
  const copy = ATLAS_DOCUMENTS[locale]
  const canonical = new URL(neutralAtlasPath(locale), canonicalOrigin).toString()
  documentRef.title = copy.title
  ensureMeta(documentRef, 'meta[name="description"]', { name: 'description', content: copy.description })
  ensureMeta(documentRef, 'meta[name="robots"]', { name: 'robots', content: 'noindex,follow' })
  ensureMeta(documentRef, 'link[rel="canonical"]', { rel: 'canonical', href: canonical })
  ensureMeta(documentRef, 'meta[property="og:url"]', { property: 'og:url', content: canonical })
  ensureMeta(documentRef, 'meta[property="og:title"]', { property: 'og:title', content: copy.title })
  ensureMeta(documentRef, 'meta[property="og:description"]', { property: 'og:description', content: copy.description })
  ensureMeta(documentRef, 'meta[name="twitter:title"]', { name: 'twitter:title', content: copy.title })
  ensureMeta(documentRef, 'meta[name="twitter:description"]', { name: 'twitter:description', content: copy.description })
  documentRef.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove())
  for (const alternateLocale of LOCALES) {
    const link = documentRef.createElement('link')
    link.rel = 'alternate'
    link.hreflang = alternateLocale
    link.href = new URL(neutralAtlasPath(alternateLocale), canonicalOrigin).toString()
    documentRef.head.append(link)
  }
  const defaultLink = documentRef.createElement('link')
  defaultLink.rel = 'alternate'
  defaultLink.hreflang = 'x-default'
  defaultLink.href = new URL(neutralAtlasPath('en'), canonicalOrigin).toString()
  documentRef.head.append(defaultLink)
}
